import { useState } from "react";
import { ArrowLeft, FileText, ShieldCheck, Cookie, Users, Mail } from "lucide-react";

const TABS = [
  { id: "terms",   label: "Regulamin",             icon: FileText },
  { id: "privacy", label: "Polityka prywatności",  icon: ShieldCheck },
  { id: "cookies", label: "Cookies",               icon: Cookie },
  { id: "about",   label: "O nas",                 icon: Users },
  { id: "contact", label: "Kontakt",               icon: Mail },
];

export default function LegalPages({ initialTab = "terms", onBack }) {
  const [tab, setTab] = useState(initialTab);
  const active = TABS.find((t) => t.id === tab) || TABS[0];

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="legal-view">
      <header className="w-full border-b border-slate-100 sticky top-0 bg-white/85 backdrop-blur z-10">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            data-testid="legal-back-button"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" /> Wróć
          </button>
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <active.icon className="w-4 h-4 text-slate-900" /> {active.label}
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 ss-fadein">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.id === tab;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                data-testid={`legal-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-full border transition-colors ${
                  isActive
                    ? "bg-[#FFD600] text-slate-900 border-[#FFD600]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-900"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        <article className="prose-slate max-w-none text-slate-700 leading-relaxed" data-testid={`legal-content-${tab}`}>
          {tab === "terms" && <TermsPl />}
          {tab === "privacy" && <PrivacyPl />}
          {tab === "cookies" && <CookiesPl />}
          {tab === "about" && <AboutPl />}
          {tab === "contact" && <ContactPl />}
        </article>
      </main>
    </div>
  );
}

/* ---------- Content sections (Polish placeholder-quality drafts) ---------- */

function H1({ children }) {
  return <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight mb-2">{children}</h1>;
}
function H2({ children }) {
  return <h2 className="text-xl font-bold text-slate-900 mt-8 mb-2">{children}</h2>;
}
function P({ children }) {
  return <p className="text-slate-700 mb-3 leading-relaxed">{children}</p>;
}
function UL({ children }) {
  return <ul className="list-disc pl-5 space-y-1 text-slate-700 mb-3">{children}</ul>;
}
function Muted({ children }) {
  return <div className="text-xs text-slate-400 mb-6">{children}</div>;
}

function TermsPl() {
  return (
    <>
      <H1>Regulamin SocialStart</H1>
      <Muted>Ostatnia aktualizacja: 5 lutego 2026</Muted>

      <H2>1. Postanowienia ogólne</H2>
      <P>Regulamin określa zasady korzystania z aplikacji webowej „SocialStart" (dalej: „Serwis"), która pomaga uczniom i studentom w nauce z wykorzystaniem sztucznej inteligencji. Właścicielem Serwisu jest SocialStart (dalej: „Usługodawca").</P>

      <H2>2. Definicje</H2>
      <UL>
        <li><b>Użytkownik</b> – osoba fizyczna korzystająca z Serwisu po zalogowaniu się kontem Google.</li>
        <li><b>Konto</b> – zbiór danych i ustawień powiązanych z Użytkownikiem.</li>
        <li><b>Subskrypcja</b> – płatny plan (Basic, Premium, Szkoły) uprawniający do generowania określonej liczby quizów miesięcznie.</li>
        <li><b>Treści AI</b> – teoria i pytania quizowe generowane automatycznie przez modele AI (OpenAI).</li>
      </UL>

      <H2>3. Rodzaj usług</H2>
      <P>Serwis udostępnia narzędzia do:</P>
      <UL>
        <li>generowania streszczeń teorii i quizów na dowolny temat,</li>
        <li>sprawdzania wiedzy poprzez testy wielokrotnego wyboru,</li>
        <li>zapisywania historii nauki i przeglądania jej,</li>
        <li>tworzenia klas dydaktycznych (w planie Szkoły).</li>
      </UL>

      <H2>4. Rejestracja i konto</H2>
      <P>Do korzystania z pełnej funkcjonalności Serwisu wymagane jest zalogowanie się kontem Google. Użytkownik zobowiązuje się do podania prawdziwych danych oraz nieudostępniania konta osobom trzecim.</P>

      <H2>5. Plany i płatności</H2>
      <P>Serwis oferuje plan darmowy (1 quiz na start) oraz płatne subskrypcje: Basic, Premium, Szkoły. Ceny są prezentowane brutto w PLN. Płatności obsługiwane są przez Stripe. Subskrypcję można anulować w każdej chwili — anulowanie kończy się na końcu bieżącego okresu rozliczeniowego.</P>

      <H2>6. Odstąpienie od umowy</H2>
      <P>Zgodnie z ustawą o prawach konsumenta, przy usługach cyfrowych świadczonych natychmiastowo po zakupie, Użytkownik może utracić prawo odstąpienia z chwilą rozpoczęcia korzystania z płatnej funkcjonalności. Szczegółowe warunki określają odrębne przepisy.</P>

      <H2>7. Odpowiedzialność za treści AI</H2>
      <P>Treści generowane przez AI mają charakter pomocniczy i mogą zawierać błędy. Użytkownik zobowiązuje się do samodzielnej weryfikacji ważnych informacji — szczególnie przed egzaminami. Usługodawca nie ponosi odpowiedzialności za skutki wykorzystania błędnych treści.</P>

      <H2>8. Zakazane zachowania</H2>
      <UL>
        <li>generowanie treści niezgodnych z prawem, obraźliwych lub naruszających prawa osób trzecich,</li>
        <li>próby obchodzenia limitów, automatyczne pobieranie treści (scraping),</li>
        <li>udostępnianie konta w celach komercyjnych bez zgody Usługodawcy.</li>
      </UL>

      <H2>9. Reklamacje</H2>
      <P>Reklamacje można zgłaszać pocztą elektroniczną na adres kontakt@socialstart.store. Rozpatrzenie następuje w terminie do 14 dni.</P>

      <H2>10. Zmiany Regulaminu</H2>
      <P>Usługodawca zastrzega sobie prawo do zmian Regulaminu. O zmianach Użytkownik zostanie poinformowany drogą elektroniczną co najmniej 7 dni przed wejściem w życie.</P>

      <H2>11. Postanowienia końcowe</H2>
      <P>W sprawach nieuregulowanych zastosowanie mają przepisy prawa polskiego. Ewentualne spory rozstrzygane będą przez właściwe sądy powszechne.</P>
    </>
  );
}

function PrivacyPl() {
  return (
    <>
      <H1>Polityka prywatności</H1>
      <Muted>Ostatnia aktualizacja: 5 lutego 2026</Muted>

      <H2>1. Administrator danych</H2>
      <P>Administratorem Twoich danych osobowych jest SocialStart (dalej: „Administrator"). Kontakt: kontakt@socialstart.store.</P>

      <H2>2. Jakie dane zbieramy</H2>
      <UL>
        <li>Dane z konta Google: imię, nazwisko, adres e-mail, zdjęcie profilowe.</li>
        <li>Dane dot. korzystania z aplikacji: tematy nauki, wyniki quizów, historia sesji.</li>
        <li>Dane techniczne: adres IP, typ przeglądarki (w logach serwera).</li>
        <li>Dane rozliczeniowe (przy płatnych subskrypcjach): przetwarzane przez Stripe — nie przechowujemy numerów kart płatniczych.</li>
      </UL>

      <H2>3. Cele przetwarzania</H2>
      <UL>
        <li>świadczenie usługi (art. 6 ust. 1 lit. b RODO),</li>
        <li>rozliczanie płatności (art. 6 ust. 1 lit. b i c RODO),</li>
        <li>obsługa klienta i reklamacje,</li>
        <li>analiza korzystania i doskonalenie produktu (art. 6 ust. 1 lit. f RODO).</li>
      </UL>

      <H2>4. Odbiorcy danych</H2>
      <UL>
        <li>OpenAI (dostawca modeli AI) – przetwarza treść zapytań w celu generowania teorii i quizów,</li>
        <li>Google (uwierzytelnianie kontem),</li>
        <li>Stripe (obsługa płatności),</li>
        <li>dostawca hostingu i baza danych.</li>
      </UL>

      <H2>5. Twoje prawa (RODO)</H2>
      <UL>
        <li>prawo dostępu do danych,</li>
        <li>prawo do sprostowania,</li>
        <li>prawo do usunięcia („prawo do bycia zapomnianym"),</li>
        <li>prawo do przenoszenia danych,</li>
        <li>prawo do sprzeciwu i ograniczenia przetwarzania,</li>
        <li>prawo do wniesienia skargi do Prezesa UODO.</li>
      </UL>

      <H2>6. Okres przechowywania</H2>
      <P>Dane przechowujemy tak długo, jak konto pozostaje aktywne, oraz przez okres wymagany przepisami (np. rozliczeniowymi). Po usunięciu konta dane są kasowane w rozsądnym terminie, poza tymi wymaganymi prawnie.</P>

      <H2>7. Dzieci i osoby nieletnie</H2>
      <P>Serwis mogą wykorzystywać osoby powyżej 13 roku życia. Osoby młodsze powinny korzystać wyłącznie za zgodą rodziców lub opiekunów prawnych.</P>

      <H2>8. Bezpieczeństwo</H2>
      <P>Stosujemy szyfrowanie HTTPS, ograniczony dostęp do baz danych i regularne aktualizacje bezpieczeństwa.</P>
    </>
  );
}

function CookiesPl() {
  return (
    <>
      <H1>Polityka cookies</H1>
      <Muted>Ostatnia aktualizacja: 5 lutego 2026</Muted>

      <H2>1. Czym są pliki cookies</H2>
      <P>Cookies to małe pliki tekstowe zapisywane w Twojej przeglądarce, które pozwalają zapamiętać informacje między wizytami.</P>

      <H2>2. Jakich cookies używamy</H2>
      <UL>
        <li><b>Niezbędne</b> – utrzymanie sesji zalogowanego użytkownika (cookie <code>session_token</code>). Bez nich Serwis nie działa poprawnie.</li>
        <li><b>Funkcjonalne</b> – zapamiętywanie preferencji, np. wybór planu miesięczny/roczny.</li>
        <li><b>Rozliczeniowe</b> – cookies Stripe podczas procesu płatności.</li>
      </UL>
      <P>Obecnie nie korzystamy z cookies marketingowych/reklamowych ani z zewnętrznej analityki (Google Analytics itp.).</P>

      <H2>3. Zarządzanie cookies</H2>
      <P>Możesz w każdej chwili wyczyścić cookies w ustawieniach swojej przeglądarki. Wyłączenie cookies niezbędnych spowoduje wylogowanie z Serwisu.</P>
    </>
  );
}

function AboutPl() {
  return (
    <>
      <H1>O SocialStart</H1>
      <Muted>Nasza historia</Muted>

      <P>SocialStart powstał, bo znamy ten ból: masz sprawdzian jutro, a przed sobą 40 stron podręcznika. Chcesz „tylko sedno" i chcesz się sprawdzić, ale nie ma czasu przekopywać się przez wszystko od zera.</P>

      <P>Zbudowaliśmy narzędzie, które robi za Ciebie to, co powinien robić dobry nauczyciel: streszcza, tłumaczy trudne pojęcia, daje przykłady z życia i pyta Cię, żeby sprawdzić, czy naprawdę rozumiesz. Do tego zapamiętuje, gdzie się pomyliłeś, i wraca do tego z Tobą.</P>

      <H2>Dla kogo jest SocialStart</H2>
      <UL>
        <li>Uczniowie liceum przed sprawdzianem lub maturą.</li>
        <li>Studenci przed kolokwium.</li>
        <li>Nauczyciele, którzy chcą przygotować szybkie powtórzenie dla klasy.</li>
        <li>Każdy, kto chce się czegoś nauczyć w kilka minut, a nie kilka godzin.</li>
      </UL>

      <H2>Nasza obietnica</H2>
      <UL>
        <li>Wszystko po polsku, jasnym językiem.</li>
        <li>Minimalistyczny design — bez migających reklam i clickbaitów.</li>
        <li>Pierwszy quiz zawsze za darmo, żebyś mógł się przekonać.</li>
      </UL>
    </>
  );
}

function ContactPl() {
  return (
    <>
      <H1>Kontakt</H1>
      <Muted>Chętnie odpowiemy</Muted>

      <div className="grid sm:grid-cols-1 gap-3">
        <a
          href="mailto:kontakt@socialstart.store"
          className="block bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-900 transition-colors"
        >
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">E-mail kontaktowy</div>
          <div className="font-bold text-slate-900">kontakt@socialstart.store</div>
          <div className="text-sm text-slate-500 mt-1">Odpowiadamy w ciągu 24-48h w dni robocze. Wsparcie techniczne, reklamacje, współpraca — wszystko na ten adres.</div>
        </a>
      </div>

      <H2>Współpraca i szkoły</H2>
      <P>
        Zainteresowany planem Szkoły dla całej klasy lub szkoły? Napisz na{" "}
        <a href="mailto:kontakt@socialstart.store" className="text-slate-900 font-bold underline">
          kontakt@socialstart.store
        </a>
        {" "}— ustalimy wygodny wariant.
      </P>

      <H2>Reklamacje</H2>
      <P>Reklamacje przyjmujemy mailowo. Rozpatrzenie następuje w terminie do 14 dni od otrzymania zgłoszenia.</P>
    </>
  );
}
